/**
 * Environment-aware logger utility
 * - Only in development (localhost): logs normally to console
 * - In staging and production: silenced (no-op)
 * - Prevents sensitive data exposure in non-development builds
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface LoggerInterface {
  log: (message?: any, ...optionalParams: any[]) => void;
  info: (message?: any, ...optionalParams: any[]) => void;
  warn: (message?: any, ...optionalParams: any[]) => void;
  error: (message?: any, ...optionalParams: any[]) => void;
  debug: (message?: any, ...optionalParams: any[]) => void;
}

class Logger implements LoggerInterface {
  private isDevelopment: boolean;

  constructor() {
    // Only enable logging in development (localhost) environment
    this.isDevelopment = process.env.REACT_APP__ENV === 'development';
  }

  private shouldLog = (): boolean => {
    return this.isDevelopment;
  }

  private logWithLevel = (level: LogLevel, message?: any, ...optionalParams: any[]): void => {
    if (this.shouldLog()) {
      console[level](message, ...optionalParams);
    }
  }

  public log = (message?: any, ...optionalParams: any[]): void => {
    this.logWithLevel('log', message, ...optionalParams);
  }

  public info = (message?: any, ...optionalParams: any[]): void => {
    this.logWithLevel('info', message, ...optionalParams);
  }

  public warn = (message?: any, ...optionalParams: any[]): void => {
    this.logWithLevel('warn', message, ...optionalParams);
  }

  public error = (message?: any, ...optionalParams: any[]): void => {
    this.logWithLevel('error', message, ...optionalParams);
  }

  public debug = (message?: any, ...optionalParams: any[]): void => {
    this.logWithLevel('debug', message, ...optionalParams);
  }

  // Helper method for API request/response logging
  public apiRequest = (config: any): void => {
    if (this.shouldLog()) {
      console.log('Request:', {
        url: config.url,
        method: config.method,
        // Only log safe headers (avoid sensitive tokens)
        headers: this.sanitizeHeaders(config.headers),
        params: config.params,
        // Don't log request data that might contain sensitive info
        hasData: !!config.data
      });
    }
  }

  public apiResponse = (response: any): void => {
    if (this.shouldLog()) {
      console.log('Response:', {
        url: response.config?.url,
        status: response.status,
        // Only log safe headers
        headers: this.sanitizeHeaders(response.headers),
        // Don't log full response data that might contain sensitive info
        hasData: !!response.data
      });
    }
  }

  public apiError = (error: any): void => {
    if (this.shouldLog()) {
      console.error('API Error:', {
        request: {
          url: error.config?.url,
          method: error.config?.method,
          headers: this.sanitizeHeaders(error.config?.headers),
          params: error.config?.params,
          hasData: !!error.config?.data
        },
        response: {
          status: error.response?.status,
          headers: this.sanitizeHeaders(error.response?.headers),
          message: error.message,
          hasErrorData: !!error.response?.data
        }
      });
    }
  }

  // Sanitize headers to remove sensitive information
  private sanitizeHeaders = (headers: any): any => {
    if (!headers || !this.shouldLog()) {
      return {};
    }

    const sensitiveHeaders = [
      'authorization',
      'bearer',
      'token',
      'tmp-token',
      'userid',
      'cookie',
      'x-api-key'
    ];

    const sanitized: any = {};
    Object.keys(headers).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (sensitiveHeaders.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = headers[key];
      }
    });

    return sanitized;
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;

// Export individual methods for convenience
export const { log, info, warn, error, debug } = logger;